import {
  CloseRounded,
  ExpandLessRounded,
  ExpandMoreRounded,
  HistoryRounded,
} from "@mui/icons-material";
import {
  Box,
  Chip,
  CircularProgress,
  Collapse,
  Drawer,
  Grid2,
  IconButton,
  Pagination,
  Stack,
} from "@mui/material";
import { observer } from "mobx-react";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import {
  formatDate,
  formatDateWithoutTime,
} from "../../../../../views/components/common/functions/formatDate";
import {
  getCouponActivityActorName,
  getCouponActivityDetailRows,
  getCouponActivitySummary,
} from "../../../../../models/SwayyCoupon/SwayyCouponActivityDisplay";
import {
  SwayyCouponActivityLog,
  SwayyCouponActivityStatus,
} from "../../../../../models/SwayyCoupon/SwayyCoupon";
import { swayyCouponStore } from "../../../stores/SwayyCouponStore";
import { swayyColors, swayyFont, swayyRadii } from "../../../theme/tokens";

const ACTIVITY_LOGS_DRAWER_Z_OFFSET = 2200;

const statusTone = (
  status: SwayyCouponActivityStatus,
): { color: string; border: string } => {
  if (status === "failure") {
    return { color: swayyColors.danger, border: swayyColors.danger };
  }
  if (status === "info") {
    return { color: swayyColors.textSecondary, border: swayyColors.border };
  }
  return { color: swayyColors.success, border: swayyColors.success };
};

const getInitials = (name: string): string => {
  const trimmed = name.trim();
  if (!trimmed) return "?";
  const parts = trimmed.split(" ").filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return trimmed[0].toUpperCase();
};

const groupLogsByDate = (
  logs: SwayyCouponActivityLog[],
): Record<string, SwayyCouponActivityLog[]> => {
  const grouped: Record<string, SwayyCouponActivityLog[]> = {};
  logs.forEach((log) => {
    const dateKey = formatDateWithoutTime(log.created_at, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
    if (!grouped[dateKey]) grouped[dateKey] = [];
    grouped[dateKey].push(log);
  });
  return grouped;
};

const ActivityLogEntry = observer(({ log }: { log: SwayyCouponActivityLog }) => {
  const store = swayyCouponStore;
  const expanded = store.isActivityLogExpanded(log.id);
  const actorName = getCouponActivityActorName(log);
  const detailRows = getCouponActivityDetailRows(log);
  const tone = statusTone(log.status);

  return (
    <Box sx={{ display: "flex", gap: 1.25 }}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: swayyRadii.full,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 11,
          fontWeight: 700,
          color: swayyColors.white,
          backgroundColor: swayyColors.deepGreen,
          fontFamily: swayyFont,
        }}
      >
        {getInitials(actorName)}
      </Box>
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          border: `1px solid ${swayyColors.border}`,
          borderRadius: swayyRadii.md,
          p: 1.5,
          bgcolor: swayyColors.white,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            flexWrap: "wrap",
            mb: 0.75,
          }}
        >
          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
            <Chip
              label={log.action_label}
              size="small"
              sx={{
                height: 22,
                fontSize: 11,
                fontWeight: 700,
                color: swayyColors.pine,
                borderColor: swayyColors.pine,
                fontFamily: swayyFont,
              }}
              variant="outlined"
            />
            <Chip
              label={log.status}
              size="small"
              sx={{
                height: 22,
                fontSize: 11,
                fontWeight: 700,
                color: tone.color,
                borderColor: tone.border,
                fontFamily: swayyFont,
                textTransform: "capitalize",
              }}
              variant="outlined"
            />
          </Box>
          <CustomTypography variant="caption" color={swayyColors.textSecondary}>
            {formatDate(log.created_at)}
          </CustomTypography>
        </Box>

        <CustomTypography
          variant="body2"
          color={swayyColors.deepGreen}
          sx={{ lineHeight: 1.5, wordBreak: "break-word" }}
        >
          {getCouponActivitySummary(log)}
        </CustomTypography>

        {detailRows.length > 0 ? (
          <>
            <Box
              onClick={() => store.toggleActivityLogExpanded(log.id)}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.25,
                mt: 1,
                cursor: "pointer",
                color: swayyColors.pine,
              }}
            >
              <CustomTypography variant="caption" color={swayyColors.pine}>
                {expanded ? "Hide details" : "View details"}
              </CustomTypography>
              {expanded ? (
                <ExpandLessRounded sx={{ fontSize: 16 }} />
              ) : (
                <ExpandMoreRounded sx={{ fontSize: 16 }} />
              )}
            </Box>
            <Collapse in={expanded}>
              <Stack
                spacing={0.75}
                sx={{
                  mt: 1,
                  borderRadius: swayyRadii.sm,
                  bgcolor: swayyColors.pineLight,
                  p: 1,
                }}
              >
                {detailRows.map((row) => (
                  <Box
                    key={`${log.id}-${row.label}`}
                    sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                  >
                    <CustomTypography
                      variant="caption"
                      color={swayyColors.deepGreen}
                      sx={{ fontWeight: 700 }}
                    >
                      {row.label}:
                    </CustomTypography>
                    <CustomTypography
                      variant="caption"
                      color={swayyColors.textSecondary}
                      sx={{ wordBreak: "break-word" }}
                    >
                      {row.value}
                    </CustomTypography>
                  </Box>
                ))}
              </Stack>
            </Collapse>
          </>
        ) : null}
      </Box>
    </Box>
  );
});

const SwayyCouponActivityLogsDrawer = () => {
  const store = swayyCouponStore;
  const couponCode = store.detail?.code;

  const handlePageChange = (_event: unknown, page: number) => {
    void store.loadActivityLogs((page - 1) * store.activityLogsLimit);
  };

  const groupedLogs = groupLogsByDate(store.activityLogs);
  const sortedDates = Object.keys(groupedLogs).sort(
    (a, b) => new Date(b).getTime() - new Date(a).getTime(),
  );

  return (
    <Drawer
      anchor="right"
      open={store.activityLogsOpen}
      onClose={store.closeActivityLogs}
      sx={{
        "& .MuiDrawer-paper": {
          borderRadius: 0,
        },
      }}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 460, md: 520 },
          maxWidth: { xs: "100vw", sm: 520 },
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          fontFamily: swayyFont,
          borderRadius: 0,
          "&.MuiPaper-root": {
            borderRadius: 0,
          },
        },
      }}
      ModalProps={{
        keepMounted: true,
        sx: {
          zIndex: (theme) =>
            theme.zIndex.drawer + ACTIVITY_LOGS_DRAWER_Z_OFFSET,
        },
        slotProps: {
          backdrop: {
            sx: {
              zIndex: (theme) =>
                theme.zIndex.drawer + ACTIVITY_LOGS_DRAWER_Z_OFFSET - 1,
            },
          },
        },
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.75,
          borderBottom: `1px solid ${swayyColors.border}`,
          backgroundColor: swayyColors.pageBg,
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap={1}
        >
          <Box display="flex" gap={1.25} alignItems="center" minWidth={0}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: swayyRadii.md,
                display: "grid",
                placeItems: "center",
                backgroundColor: swayyColors.pineLight,
                color: swayyColors.pine,
              }}
            >
              <HistoryRounded fontSize="small" />
            </Box>
            <Box minWidth={0}>
              <CustomTypography
                variant="heading6"
                color={swayyColors.deepGreen}
                sx={{ fontWeight: 700 }}
              >
                Activity Logs
              </CustomTypography>
              {couponCode ? (
                <CustomTypography
                  variant="caption"
                  color={swayyColors.textSecondary}
                  sx={{ display: "block" }}
                >
                  {couponCode}
                </CustomTypography>
              ) : null}
            </Box>
          </Box>
          <IconButton
            aria-label="Close activity logs"
            onClick={store.closeActivityLogs}
            size="small"
            sx={{
              flexShrink: 0,
              color: swayyColors.deepGreen,
              border: `1px solid ${swayyColors.border}`,
              borderRadius: swayyRadii.sm,
              backgroundColor: swayyColors.white,
              "&:hover": {
                backgroundColor: swayyColors.pineLight,
              },
            }}
          >
            <CloseRounded />
          </IconButton>
        </Box>
      </Box>

      <Box sx={{ flex: 1, overflow: "auto", px: 2, py: 2 }}>
        {store.activityLogsLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 240,
            }}
          >
            <CircularProgress size={32} sx={{ color: swayyColors.pine }} />
          </Box>
        ) : store.activityLogsError ? (
          <CustomTypography variant="body2" color={swayyColors.danger}>
            {store.activityLogsError}
          </CustomTypography>
        ) : store.activityLogs.length === 0 ? (
          <Stack alignItems="center" spacing={1} sx={{ py: 8 }}>
            <HistoryRounded sx={{ fontSize: 42, color: swayyColors.border }} />
            <CustomTypography
              variant="body2"
              color={swayyColors.textSecondary}
            >
              No activity recorded for this coupon yet.
            </CustomTypography>
          </Stack>
        ) : (
          sortedDates.map((date) => (
            <Box key={date} sx={{ mb: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 2,
                }}
              >
                <Box
                  sx={{
                    height: "1px",
                    flex: 1,
                    backgroundColor: swayyColors.border,
                  }}
                />
                <CustomTypography
                  variant="caption"
                  color={swayyColors.textSecondary}
                  sx={{
                    px: 1.25,
                    py: 0.4,
                    fontWeight: 700,
                    backgroundColor: swayyColors.pineLight,
                    borderRadius: swayyRadii.full,
                  }}
                >
                  {date}
                </CustomTypography>
                <Box
                  sx={{
                    height: "1px",
                    flex: 1,
                    backgroundColor: swayyColors.border,
                  }}
                />
              </Box>
              <Stack spacing={1.5}>
                {groupedLogs[date].map((log) => (
                  <ActivityLogEntry key={log.id} log={log} />
                ))}
              </Stack>
            </Box>
          ))
        )}
      </Box>

      {store.activityLogsTotal > 0 ? (
        <Grid2
          container
          spacing={1}
          alignItems="center"
          justifyContent="space-between"
          sx={{
            px: 2,
            py: 1.5,
            borderTop: `1px solid ${swayyColors.border}`,
          }}
        >
          <Grid2 size={{ xs: 12, sm: "auto" }}>
            <CustomTypography
              variant="caption"
              color={swayyColors.textSecondary}
            >
              {store.activityLogsTotal}{" "}
              {store.activityLogsTotal === 1 ? "entry" : "entries"}
            </CustomTypography>
          </Grid2>
          <Grid2 size={{ xs: 12, sm: "auto" }}>
            <Pagination
              count={Math.max(store.activityLogsTotalPages, 1)}
              page={store.activityLogsPage}
              disabled={store.activityLogsLoading}
              onChange={handlePageChange}
              size="small"
              sx={{
                "& .MuiPaginationItem-root.Mui-selected": {
                  backgroundColor: swayyColors.pine,
                  color: swayyColors.white,
                },
              }}
            />
          </Grid2>
        </Grid2>
      ) : null}
    </Drawer>
  );
};

export default observer(SwayyCouponActivityLogsDrawer);
